import { useTranslation } from "react-i18next";
import { SVG } from "../../../../../constants/assetLists";
import { useOperationsContext } from "../../../../../context/OperationsContextProvider";
import { useMemo, useState } from "react";
import AnnouncementFilter from "./AnnouncementFilter";
import useScreenSize from "../../../../../hooks/useScreenSize";
import AppConfirmationDialog from "../../../../../components/common/AppConfirmationDialog";
import { useAnnouncementTaskMutations } from "../../../../../hooks/useAnnouncementTaskMutations";

const filterMap: Record<string, string> = {
  all: "הכל",
  regulation: "רגולציה",
  restaurant: "מסעדה",
  kitchen: "מטבח",
  bar: "בר",
  administration: "אדמינסטרציה",
};

function Header() {
  const { i18n } = useTranslation();
  const isHebrew = useMemo(() => i18n.language === "heb", [i18n.language]);

  const screenSize = useScreenSize();
  const isLarge = screenSize == "xl" || screenSize == "2xl";

  const {
    announcementFilterType,
    announcementFilterLocation,
    announcementFilterLocationLabel,
    announcementSearchText,
    setAnnouncementSearchText,
    announcementStatsData,
    announcementTypeFilter,
    setAnnouncementTypeFilter,
    selectedAnnouncementIds,
    announcementData,
    regulationData,
    announcementUrgentData,
    daySummaryAnnouncementFilter,
    clearDaySummaryAnnouncementFilter,
  } = useOperationsContext();
  const { bulkDeleteAnnouncementsMutation } = useAnnouncementTaskMutations();

  /* ids stay in the selection after their announcement is gone (deleted from
     its own menu, filtered out, searched away), so only the ones still on
     screen can take part in the delete */
  const visibleSelectedIds = useMemo(() => {
    if (selectedAnnouncementIds.size === 0) return [];

    return [...regulationData, ...announcementUrgentData, ...announcementData]
      .filter((announcement: any) =>
        selectedAnnouncementIds.has(announcement._id),
      )
      .map((announcement: any) => announcement._id);
  }, [
    selectedAnnouncementIds,
    regulationData,
    announcementUrgentData,
    announcementData,
  ]);

  const selectedAnnouncementCount = visibleSelectedIds.length;

  const deleteDialogSubTitle =
    selectedAnnouncementCount === 1
      ? "האם למחוק את ההודעה?"
      : `${selectedAnnouncementCount} הודעות יימחקו, האם למחוק?`;

  const [openFilters, setOpenFilters] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] =
    useState(false);

  const filterText = useMemo(() => {
    if (announcementFilterType === "all") return filterMap["all"] || "הכל";

    if (announcementStatsData?.data?.byDepartment) {
      const department = announcementStatsData.data.byDepartment.find(
        (dept: any) => dept.departmentId === announcementFilterType,
      );
      if (department) return department.departmentName;
    }

    return filterMap[announcementFilterType] || announcementFilterType;
  }, [announcementFilterType, announcementStatsData]);

  const breadcrumbItems = useMemo(
    () =>
      [
        announcementFilterLocation?.nodeType !== "client"
          ? announcementFilterLocationLabel
          : null,
        filterText,
      ].filter(Boolean),
    [
      announcementFilterLocation?.nodeType,
      announcementFilterLocationLabel,
      filterText,
    ],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnnouncementSearchText(e.target.value);
  };

  const handleFilter = () => {
    setOpenFilters((prev) => !prev);
  };

  const handleClearFilter = () => setAnnouncementTypeFilter("");
  const handleClearDaySummaryFilter = () => {
    clearDaySummaryAnnouncementFilter();
    setAnnouncementSearchText("");
    setAnnouncementTypeFilter("");
  };

  /* the selected action should be visible as soon as the user selects a card */
  const handleOpenDeleteConfirmationDialog = () => {
    if (
      selectedAnnouncementCount < 1 ||
      bulkDeleteAnnouncementsMutation.isPending
    )
      return;

    setShowDeleteConfirmationDialog(true);
  };

  const handleCloseDeleteConfirmationDialog = () => {
    setShowDeleteConfirmationDialog(false);
  };

  const handleConfirmBulkDelete = () => {
    if (
      visibleSelectedIds.length === 0 ||
      bulkDeleteAnnouncementsMutation.isPending
    ) {
      handleCloseDeleteConfirmationDialog();
      return;
    }

    setShowDeleteConfirmationDialog(false);
    bulkDeleteAnnouncementsMutation.mutate(visibleSelectedIds);
  };

  return (
    <>
      <div
        className={`w-full relative py-[9px] bg-white px-4 shadow-md ${
          isLarge ? (isHebrew ? "rounded-tl-[32px]" : "rounded-tr-[32px]") : ""
        } z-[2]`}
      >
        <div className="w-full h-full">
          <div className="w-full h-full flex flex-wrap items-center extraSmall:gap-2 md:gap-5 xl:gap-[100px]">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-semibold leading-6 text-black">
                התראות ומשימות
              </span>
              {breadcrumbItems.map((item, index) => (
                <div
                  key={`${item}-${index}`}
                  className="flex items-center gap-2"
                >
                  <span className="text-2xl font-light leading-6 text-tealBlue">
                    &gt;
                  </span>
                  <span
                    className={`text-2xl font-semibold leading-6 ${
                      index === breadcrumbItems.length - 1
                        ? "text-tealBlue"
                        : "text-black"
                    }`}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex-1 flex items-center gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="חיפוש"
                  className={`w-full py-[6px] rounded-[28px] border border-lightDarkGray focus:outline-none focus:bg-gray95 text-[16px] font-normal placeholder:text-black ${
                    isHebrew ? "pr-3 pl-[40px]" : "pl-3 pr-[40px]"
                  }`}
                  value={announcementSearchText}
                  onChange={handleSearch}
                />
                <div
                  className={`absolute top-1/2 transform -translate-y-1/2 flex gap-2 items-center ${
                    isHebrew ? "left-3" : "right-3"
                  }`}
                >
                  {announcementSearchText && (
                    <div
                      onClick={() => setAnnouncementSearchText("")}
                      className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer"
                    >
                      <img
                        src={SVG.closeDarkIcon}
                        alt="Clear"
                        className="w-[12px] h-[12px] object-contain"
                      />
                    </div>
                  )}
                  <div
                    className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                      announcementSearchText && "bg-aqua"
                    }`}
                  >
                    <img
                      src={SVG.searchIcon}
                      alt="Search"
                      className="w-[18px] h-[18px] object-contain"
                    />
                  </div>
                </div>
              </div>
              {selectedAnnouncementCount > 0 && (
                <button
                  className="w-[48px] h-[48px] rounded-full bg-gray95 flex items-center justify-center"
                  onClick={handleOpenDeleteConfirmationDialog}
                  disabled={bulkDeleteAnnouncementsMutation.isPending}
                >
                  <img
                    src={SVG.listDeleteRedIcon}
                    alt="delete"
                    className="w-[24px] h-[24px] object-contain"
                  />
                </button>
              )}

              {announcementTypeFilter !== "" && (
                <button
                  type="button"
                  onClick={handleClearFilter}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 bg-pastelYellow "
                >
                  <span className="text-sm text-[#1D192B]">ביטול פילטר</span>
                  <img
                    src={SVG.closeDarkIcon}
                    alt="clear"
                    className="w-[11px] h-[11px] object-contain"
                  />
                </button>
              )}

              {daySummaryAnnouncementFilter && (
                <button
                  type="button"
                  onClick={handleClearDaySummaryFilter}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 bg-pastelYellow "
                >
                  <span className="text-sm text-[#1D192B]">
                    {daySummaryAnnouncementFilter.clearLabel ??
                      "ביטול סינון סיכום"}
                  </span>
                  <img
                    src={SVG.closeDarkIcon}
                    alt="clear"
                    className="w-[11px] h-[11px] object-contain"
                  />
                </button>
              )}

              {/* <button
              className={`flex items-center gap-2 py-2 px-3 rounded-[8px] cursor-pointer hover:bg-gray90 transition-colors duration-200 ${
                openFilters && 'bg-gray90'
              }`}
              onClick={handleFilter}>
              <div className="w-[24px] h-[24px] flex justify-center items-center">
                <img
                  src={SVG.filterIcon}
                  alt="search"
                  className="w-full h-full object-contain"
                />
              </div>

              <span className="text-[16px] text-grayText">סינון מתקדם</span>
            </button> */}
            </div>
          </div>
        </div>

        {/* filter */}
        <AnnouncementFilter open={openFilters} onClose={handleFilter} />
      </div>

      <AppConfirmationDialog
        open={showDeleteConfirmationDialog}
        onClose={handleCloseDeleteConfirmationDialog}
        onConfirm={handleConfirmBulkDelete}
        icon={
          <img
            src={SVG.deleteRedIcon}
            alt="delete"
            className="w-[48px] h-[48px]"
          />
        }
        title="שימו לב"
        subTitle={deleteDialogSubTitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default Header;
